import { AsYouType, getCountryCallingCode, isPossiblePhoneNumber } from "libphonenumber-js";
var ROR_BASE = "https://api.ror.org/v2/organizations";
var PHONE_INFO = {
    US: { dialCode: "+1", example: "(201) 555-0123" },
    CA: { dialCode: "+1", example: "(506) 234-5678" },
    GB: { dialCode: "+44", example: "07400 123456" },
    IE: { dialCode: "+353", example: "085 012 3456" },
    DE: { dialCode: "+49", example: "030 1234567" },
    FR: { dialCode: "+33", example: "06 12 34 56 78" },
    CH: { dialCode: "+41", example: "079 123 45 67" },
    AT: { dialCode: "+43", example: "0664 123456" },
    NL: { dialCode: "+31", example: "06 12345678" },
    BE: { dialCode: "+32", example: "0470 12 34 56" },
    ES: { dialCode: "+34", example: "612 34 56 78" },
    IT: { dialCode: "+39", example: "312 345 6789" },
    PT: { dialCode: "+351", example: "912 345 678" },
    SE: { dialCode: "+46", example: "070 123 45 67" },
    NO: { dialCode: "+47", example: "406 12 345" },
    DK: { dialCode: "+45", example: "20 12 34 56" },
    FI: { dialCode: "+358", example: "041 2345678" },
    PL: { dialCode: "+48", example: "512 345 678" },
    CZ: { dialCode: "+420", example: "601 123 456" },
    IL: { dialCode: "+972", example: "050-123-4567" },
    JP: { dialCode: "+81", example: "090-1234-5678" },
    CN: { dialCode: "+86", example: "131 2345 6789" },
    KR: { dialCode: "+82", example: "010-1234-5678" },
    TW: { dialCode: "+886", example: "0912 345 678" },
    HK: { dialCode: "+852", example: "5123 4567" },
    SG: { dialCode: "+65", example: "8123 4567" },
    IN: { dialCode: "+91", example: "081234 56789" },
    AU: { dialCode: "+61", example: "0412 345 678" },
    NZ: { dialCode: "+64", example: "021 123 4567" },
    BR: { dialCode: "+55", example: "(11) 91234-5678" },
    MX: { dialCode: "+52", example: "222 123 4567" }
};
var rorCache = new Map();
var wait = function (milliseconds) { return new Promise(function (resolve) { return window.setTimeout(resolve, milliseconds); }); };
var fetchWithRetry = function (url, signal, label, attempt) {
    if (attempt === void 0) { attempt = 0; }
    return fetch(url, { signal: signal, headers: { Accept: "application/json" } }).then(function (response) {
        if (response.ok) {
            return response;
        }
        if (attempt < 1 && !(signal && signal.aborted)) {
            return wait(350).then(function () { return fetchWithRetry(url, signal, label, attempt + 1); });
        }
        throw new Error(label + " " + response.status);
    });
};
var displayName = function (names) {
    var display = names && names.find(function (name) { return Array.isArray(name.types) && name.types.indexOf("ror_display") >= 0; });
    return display ? display.value : names && names[0] ? names[0].value : "";
};
var searchableNames = function (names) { return (Array.isArray(names) ? names : [])
    .map(function (name) { return String(name && name.value || "").trim(); })
    .filter(Boolean)
    .filter(function (value, index, values) { return values.indexOf(value) === index; }); };
export var rorOrganizationFromApi = function (item) {
    var names = Array.isArray(item.names) ? item.names : [];
    var locations = Array.isArray(item.locations) ? item.locations : [];
    var locationDetails = locations
        .map(function (location) { return location && location.geonames_details ? location.geonames_details : null; })
        .filter(Boolean);
    var singleLocation = locationDetails.length === 1 ? locationDetails[0] : {};
    var countryCodes = locationDetails
        .map(function (location) { return location.country_code; })
        .filter(Boolean)
        .filter(function (value, index, values) { return values.indexOf(value) === index; });
    var countries = locationDetails
        .map(function (location) { return location.country_name; })
        .filter(Boolean)
        .filter(function (value, index, values) { return values.indexOf(value) === index; });
    return {
        id: String(item.id || ""),
        name: displayName(names),
        searchNames: searchableNames(names),
        city: singleLocation.name || undefined,
        country: countries.length === 1 ? countries[0] : undefined,
        countryCode: countryCodes.length === 1 ? countryCodes[0] : undefined,
        region: singleLocation.country_subdivision_name || undefined,
        regionCode: singleLocation.country_subdivision_code || undefined,
        locationCount: locationDetails.length,
        types: Array.isArray(item.types) ? item.types : []
    };
};
export var searchOrganizations = function (query, signal, preferredCountry) {
    var q = query.trim();
    if (q.length < 3) {
        return Promise.resolve([]);
    }
    var country = (preferredCountry || "").toUpperCase();
    var key = country + "::" + q.toLowerCase();
    var cached = rorCache.get(key);
    if (cached) {
        return Promise.resolve(cached);
    }
    var url = new URL(ROR_BASE);
    url.searchParams.set("query", rorQueryForInput(q));
    return fetchWithRetry(url.toString(), signal, "ROR").then(function (response) { return response.json(); }).then(function (json) {
        var organizations = (Array.isArray(json.items) ? json.items : [])
            .filter(function (item) { return item.status === undefined || item.status === "active"; })
            .map(rorOrganizationFromApi)
            .filter(function (organization) { return Boolean(organization.name); });
        var ranked = rankOrganizations(organizations, q, country).slice(0, 3);
        if (ranked.length > 0) {
            rorCache.set(key, ranked);
        }
        return ranked;
    });
};
export var rankOrganizations = function (organizations, query, preferredCountry) {
    var country = (preferredCountry || "").toUpperCase();
    var scored = organizations.map(function (organization, index) { return ({
        organization: organization,
        index: index,
        relevance: organizationRelevance(organization, query),
        preferredLocation: Boolean(country) && organization.countryCode === country
    }); });
    var hasClearTextMatch = scored.some(function (entry) { return entry.relevance.clearTextMatch; });
    return scored
        .filter(function (entry) { return !hasClearTextMatch || entry.relevance.clearTextMatch; })
        .sort(function (a, b) {
        return Number(b.relevance.exactName) - Number(a.relevance.exactName)
            || Number(b.relevance.completeNamePrefix) - Number(a.relevance.completeNamePrefix)
            || Number(b.relevance.displayTokensMatched) - Number(a.relevance.displayTokensMatched)
            || Number(b.relevance.orderedPrefix) - Number(a.relevance.orderedPrefix)
            || Number(b.relevance.allTokensMatched) - Number(a.relevance.allTokensMatched)
            || b.relevance.matchedTokens - a.relevance.matchedTokens
            || b.relevance.exactTokens - a.relevance.exactTokens
            || Number(b.preferredLocation) - Number(a.preferredLocation)
            || a.index - b.index;
    })
        .map(function (entry) { return entry.organization; });
};
var normalizeOrganizationName = function (value) {
    return String(value || "")
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[.,/#!$%^&*;:{}=\-_`~()]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
};
var organizationQueryTokens = function (value) {
    return normalizeOrganizationName(value).split(" ").filter(Boolean);
};
export var rorQueryForInput = function (query) {
    var tokens = normalizeOrganizationName(query).split(" ").filter(Boolean);
    if (tokens.length === 0) {
        return "";
    }
    var expansions = {
        uni: "university",
        univ: "university",
        inst: "institute",
        hosp: "hospital"
    };
    var lastIndex = tokens.length - 1;
    tokens[lastIndex] = expansions[tokens[lastIndex]] || tokens[lastIndex];
    return tokens.join(" ");
};
export var organizationRelevance = function (organization, query) {
    var normalizedQuery = normalizeOrganizationName(query);
    var names = organization.searchNames.concat(organization.name).map(normalizeOrganizationName);
    var queryTokens = organizationQueryTokens(query);
    var tokenizedNames = names.map(function (name) { return name.split(" ").filter(Boolean); });
    var displayNameTokens = normalizeOrganizationName(organization.name).split(" ").filter(Boolean);
    var nameTokens = names.reduce(function (tokens, name) {
        return tokens.concat(name.split(" ").filter(Boolean));
    }, []);
    var matchedTokens = 0;
    var exactTokens = 0;
    queryTokens.forEach(function (queryToken) {
        if (nameTokens.some(function (nameToken) { return nameToken === queryToken; })) {
            matchedTokens += 1;
            exactTokens += 1;
        }
        else if (nameTokens.some(function (nameToken) { return nameToken.indexOf(queryToken) === 0; })) {
            matchedTokens += 1;
        }
    });
    var orderedPrefix = tokenizedNames.some(function (tokens) {
        return tokens.length >= queryTokens.length
            && queryTokens.every(function (queryToken, index) { return tokens[index].indexOf(queryToken) === 0; });
    });
    var completeNamePrefix = tokenizedNames.some(function (tokens) {
        return tokens.length === queryTokens.length
            && queryTokens.every(function (queryToken, index) { return tokens[index].indexOf(queryToken) === 0; });
    });
    var displayTokensMatched = queryTokens.length > 0 && queryTokens.every(function (queryToken) {
        return displayNameTokens.some(function (nameToken) { return nameToken.indexOf(queryToken) === 0; });
    });
    var exactName = normalizedQuery.length > 0 && names.indexOf(normalizedQuery) >= 0;
    return {
        exactName: exactName,
        completeNamePrefix: completeNamePrefix,
        displayTokensMatched: displayTokensMatched,
        clearTextMatch: exactName || completeNamePrefix || displayTokensMatched,
        orderedPrefix: orderedPrefix,
        allTokensMatched: queryTokens.length > 0 && matchedTokens === queryTokens.length,
        matchedTokens: matchedTokens,
        exactTokens: exactTokens
    };
};
export var isLikelyOrganizationMatch = function (organization, query) {
    var relevance = organizationRelevance(organization, query);
    return relevance.exactName || (organizationQueryTokens(query).length > 1 && relevance.completeNamePrefix);
};
export var isStrongOrganizationMatch = function (organization, query) {
    var normalizedQuery = normalizeOrganizationName(query);
    return normalizedQuery.length >= 3 && organization.searchNames
        .concat(organization.name)
        .some(function (name) { return normalizeOrganizationName(name) === normalizedQuery; });
};
export var countryCodeFromLocales = function (locales) {
    for (var _i = 0, locales_1 = locales; _i < locales_1.length; _i++) {
        var locale = locales_1[_i];
        var subtags = String(locale || "").replace(/_/g, "-").split("-");
        var region = subtags.slice(1).find(function (subtag) { return /^[A-Za-z]{2}$/.test(subtag); });
        if (region) {
            return region.toUpperCase();
        }
    }
    return undefined;
};
export var browserLocaleCountryCode = function () {
    if (typeof navigator === "undefined") {
        return undefined;
    }
    var locales = navigator.languages && navigator.languages.length > 0
        ? Array.prototype.slice.call(navigator.languages)
        : [navigator.language];
    return countryCodeFromLocales(locales);
};
var ROR_TYPE_LABELS = {
    education: "University",
    government: "Government lab",
    healthcare: "Hospital",
    nonprofit: "Nonprofit",
    company: "Company",
    facility: "Facility",
    archive: "Archive"
};
export var rorTypeLabel = function (organization) {
    return organization.types.map(function (value) { return ROR_TYPE_LABELS[value.toLowerCase()]; }).find(Boolean) || "";
};
export var rorLocationDescription = function (organization) {
    return organization.locationCount > 1
        ? [organization.country, organization.locationCount + " locations"].filter(Boolean).join(" · ")
        : [organization.city, organization.region, organization.country].filter(Boolean).join(", ");
};
export var categoryForRorTypes = function (types) {
    var normalized = types.map(function (type) { return type.toLowerCase(); });
    if (normalized.indexOf("education") >= 0) {
        return "UNIVERSITY";
    }
    if (normalized.indexOf("government") >= 0) {
        return "GOVT_LAB";
    }
    if (normalized.indexOf("healthcare") >= 0) {
        return "RESEARCH_HOSPITAL";
    }
    if (normalized.indexOf("nonprofit") >= 0) {
        return "NONPROFIT_INST";
    }
    if (normalized.indexOf("company") >= 0) {
        return "COMPANY";
    }
    return "";
};
export var rorDescription = function (organization) {
    return [rorLocationDescription(organization), rorTypeLabel(organization)].filter(Boolean).join(" · ");
};
var countryForPhone = function (countryCode) {
    var normalized = (countryCode || "").toUpperCase();
    return normalized.length === 2 ? normalized : undefined;
};
export var phoneInfoFor = function (countryCode) {
    var country = countryForPhone(countryCode);
    var dialCode = "";
    if (country) {
        try {
            dialCode = "+" + getCountryCallingCode(country);
        }
        catch (_) {
            dialCode = "";
        }
    }
    var known = PHONE_INFO[(countryCode || "").toUpperCase()];
    return { dialCode: dialCode, example: known ? known.example : "" };
};
export var formatPhoneNumberInput = function (value, countryCode) {
    if (!value) {
        return "";
    }
    try {
        return new AsYouType(countryForPhone(countryCode)).input(value);
    }
    catch (_) {
        return value;
    }
};
export var isPossibleLocalizedPhoneNumber = function (value, countryCode) {
    var country = countryForPhone(countryCode);
    if (!value.trim() || (!country && value.trim().charAt(0) !== "+")) {
        return false;
    }
    try {
        return isPossiblePhoneNumber(value, country);
    }
    catch (_) {
        return false;
    }
};
//# sourceMappingURL=OrganizationLookup.js.map


//////////////////
// WEBPACK FOOTER
// ../common/build/dist/Registration/OrganizationLookup.js
// module id = 1387
// module chunks = 0